import { Label } from '@/components/ui/label';
import { humanizeError } from '@/lib/ui';
import { cn } from '@/lib/utils';
import { type ReactNode, useId } from 'react';

interface FormFieldProps {
  label: ReactNode;
  /** Helper text rendered below the input. Supports plain text or nodes. */
  hint?: ReactNode;
  error?: unknown;
  required?: boolean;
  optional?: boolean;
  className?: string;
  /** Render-prop receives the id to bind to the input. */
  children: (id: string) => ReactNode;
}

export function FormField({
  label,
  hint,
  error,
  required,
  optional,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const errorMessage = error ? humanizeError(error) : null;
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id} className="text-xs font-medium text-fg-soft">
          {label}
          {required ? (
            <span className="ml-0.5 text-danger" aria-label="required">
              *
            </span>
          ) : null}
          {optional ? (
            <span className="ml-1 font-normal text-muted-foreground">(optional)</span>
          ) : null}
        </Label>
      </div>
      {children(id)}
      {hint && !errorMessage ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {errorMessage ? (
        <p className="text-xs text-danger" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
